import { Button, FormControl, Grid, Link, makeStyles, MenuItem, Select, Snackbar, Typography } from '@material-ui/core';
import { createTitle, deleteTitleById, getTitles, resetTitleStatuses, updateTitle, getTitleTreeByNumber } from '../../api/title';
import { getNodeById, saveNodeById } from '../../api/node';
import { Alert } from '../../components/Alert/Alert';
import { ConfirmationDialog } from '../../components/Dialogs/ConfirmationDialog';
import { TableMeta, Title, TitleStatus, User, UserRole } from '../../domain/domain';
import MUIDataTable from 'mui-datatables';
import React, { useEffect, useState } from 'react';
import { compose } from "redux";
import { connect } from "react-redux";
import { Link as RouterLink } from 'react-router-dom';
import { ApplicationState } from '../../reducers';
import AddTitleDialog from './AddTitleDialog';
import { UiActionType } from '../../reducers/ui.reducer';
import NotFound from '../../components/NotFound';
import { SNACKBAR_LONG_AUTO_HIDE_DURATION_MS } from '../../api/common';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

const useStyles = makeStyles(_ => ({
    root: {
        maxWidth: '80%',
        marginLeft: 'auto',
        marginRight: 'auto',
        marginTop: 20
    },
    tableWrapper: {
        width: 800,
        '& th': {
            '&:nth-child(1)': {
                width: 100
            },
            '&:nth-child(2)': {
                width: 500
            }
        }
    },
    titleColumn: {
    },
    grow: {
        flexGrow: 1
    },
    header: {
        color: '#71767a', /* $uswds-grayscale(5) */
        fontWeight: 'bolder',
        pointerEvents: 'none'
    },
    subitem: {
        paddingLeft: 50
    }
}));

type Props = {
    currentUser: User,
    dispatch: (msg: any) => void
}

const TitleStatusPage: React.FC<Props> = ({ currentUser, dispatch }) => {
    const classes = useStyles();
    const [records, setRecords] = useState<Title[]>([]);
    const [snackBarOpen, setSnackBarOpen] = useState<boolean>(false);
    const [snackBarMessage, setSnackBarMessage] = useState<string>("");
    const [resetDialogOpen, setResetDialogOpen] = useState<boolean>(false);
    const [resetStatus, setResetStatus] = useState<TitleStatus>(TitleStatus.WAITING_FOR_ASSIGNMENT);
    const [addTitleDialogOpen, setAddTitleDialogOpen] = useState<boolean>(false);
    const [deleteTitleDialogOpen, setDeleteTitleDialogOpen] = useState<boolean>(false);
    const [selectedTitleId, setSelectedTitleId] = useState<number | undefined>(undefined);

    useDocumentTitle("Title Status");

    const titleStatusRenderer = (value: any, tableMeta: TableMeta) => {
        return (
            <FormControl size='small' fullWidth={true}>
                <Select
                    variant='outlined'
                    labelId="title-status-label"
                    id="title-status-select"
                    value={value}
                    onChange={(event) => {
                        let newValue = event.target.value as TitleStatus;
                        let title = records.find(obj => { return obj.id === tableMeta.rowData[0] });
                        if (typeof title !== 'undefined') {
                            title.titleStatus = newValue;
                            updateTitle(title.id, title).then(response => {
                                let newRecords = [...records];
                                let idx = newRecords.findIndex(r => r.id === response.data.id);
                                if (idx >= 0) {
                                    newRecords[idx] = response.data;
                                    setRecords(newRecords);
                                    setSnackBarMessage("Title Status updated.");
                                    setSnackBarOpen(true);
                                }
                            }).catch(error => {
                                console.error(error);
                            })
                        }
                    }}
                >
                    <MenuItem value={TitleStatus.WAITING_FOR_ASSIGNMENT}>Waiting for Assignment</MenuItem>
                    <MenuItem value={TitleStatus.ASSIGNED}>Assigned</MenuItem>
                    <MenuItem value={TitleStatus.IN_PROGRESS}>In Progress</MenuItem>
                    <MenuItem value={undefined} className={classes.header}>Ready for Review</MenuItem>
                    <MenuItem value={TitleStatus.ATTORNEY_REVIEW} className={classes.subitem}>Attorney Review</MenuItem>
                    <MenuItem value={TitleStatus.LE_REVIEW_OF_ATTORNEY_CHANGES} className={classes.subitem}>LE Review of Attorney Changes</MenuItem>
                    <MenuItem value={TitleStatus.PUB_TEAM_REVIEW} className={classes.subitem}>Pub Team Review</MenuItem>
                    <MenuItem value={TitleStatus.READY_TO_PUBLISH}>Ready to Publish</MenuItem>
                    <MenuItem value={TitleStatus.PUBLISH}>Publish</MenuItem>
                </Select>
            </FormControl>
        )
    }

    const columns = [
        {
            name: 'id',
            options: {
                display: false,
            }
        },
        {
            name: 'titleNumber',
            label: 'Title Number',
            options: {
                customBodyRender: (value: any, tableMeta: TableMeta) => {
                    return <Link className={classes.titleColumn} component={RouterLink} to={`/component/${value === '0' || value === 0 ? '00' : value}`}>{value}</Link>;
                },
                sortCompare: (order: string) => {
                    return (obj1: any, obj2: any) => {
                        let val1 = parseFloat(obj1.data);
                        let val2 = parseFloat(obj2.data);
                        return (val1 - val2) * (order === 'asc' ? 1 : -1);
                    };
                }
            }
        },
        { name: 'titleName', label: 'Title Name' },
        {
            name: 'titleStatus',
            label: 'Current Status',
            options: {
                customBodyRender: titleStatusRenderer
            }
        },
        {
            name: "deleteButton",
            label: "Delete Title",
            sort: false,
            options:
            {
                display: currentUser.userRole === UserRole.ADMINISTRATOR,
                customBodyRender: (value: any, tableMeta: TableMeta) => {
                    return (
                        <Button
                            color='primary'
                            size='small'
                            variant='contained'
                            onClick={() => {
                                setDeleteTitleDialogOpen(true);
                                setSelectedTitleId(tableMeta.rowData[0]);
                            }}
                        >Delete Title
                        </Button>
                    );
                }
            }
        }
    ]

    useEffect(() => {
        getTitles().then(response => {
            setRecords(response.data);
        }).catch(error => {
            console.error(error);
        })
    }, []);

    const onResetAllStatuses = () => {
        resetTitleStatuses(resetStatus).then(response => {
            setSnackBarMessage("Title statuses reset to Waiting for Assignment");
            setSnackBarOpen(true);
            setRecords(response.data);
        }).catch(error => {
            console.error(error);
        })
    }

    if (currentUser.userRole === UserRole.NON_PUBLICATIONS_TEAM) {
        return <NotFound />
    }

    return (
        <>
            <Snackbar open={snackBarOpen} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} autoHideDuration={SNACKBAR_LONG_AUTO_HIDE_DURATION_MS} onClose={() => { setSnackBarOpen(false) }}>
                <Alert onClose={() => { setSnackBarOpen(false) }} severity={"success"}>
                    {snackBarMessage}
                </Alert>
            </Snackbar>
            <ConfirmationDialog
                open={resetDialogOpen}
                title="Reset Title Statuses"
                contentText={"Are you sure you want to reset all title statuses to " + (resetStatus === TitleStatus.WAITING_FOR_ASSIGNMENT ? "Waiting for Assignment" : "Assigned") + "?"}
                handleNo={() => {
                    setResetDialogOpen(false);
                }}
                handleYes={() => {
                    onResetAllStatuses();
                    setResetDialogOpen(false);
                }}
            />
            <ConfirmationDialog
                open={deleteTitleDialogOpen}
                title="Delete Title"
                contentText="Are you sure you want to delete the title?"
                handleNo={() => {
                    setDeleteTitleDialogOpen(false);
                }}
                handleYes={() => {
                    setDeleteTitleDialogOpen(false);
                    if (!selectedTitleId) {
                        return;
                    }
                    deleteTitleById(selectedTitleId).then(response => {
                        dispatch({ type: UiActionType.SNACKBAR_STATE_CHANGED, newState: { open: true, message: "Title deleted", severity: "success" } });
                        let newRecords = [...records];
                        let idx = newRecords.findIndex(r => r.id === selectedTitleId);
                        if (idx >= 0) {
                            newRecords.splice(idx, 1);
                        }
                        setRecords(newRecords);
                    }).catch(error => {
                        console.error(error);
                        dispatch({ type: UiActionType.SNACKBAR_STATE_CHANGED, newState: { open: true, message: "Could not delete title", severity: "error" } });
                    })
                }}
            />
            <AddTitleDialog
                open={addTitleDialogOpen}
                onOK={(title) => {
                    setAddTitleDialogOpen(false);
                    createTitle(title).then(response => {
                        dispatch({ type: UiActionType.SNACKBAR_STATE_CHANGED, newState: { open: true, message: "Title added", severity: "success" } });
                        let newRecords = [...records];
                        newRecords.push(response.data);
                        setRecords(newRecords);
                        getTitleTreeByNumber(response.data.titleNumber).then(response => {
                            let nodeId = response.data.nodeId;
                            getNodeById(nodeId).then(response => {
                                let editorNode = response.data;
                                editorNode.workingCopyCitation = editorNode.citation;
                                saveNodeById(editorNode).then(_ => {
                                }).catch(error => {
                                    console.error(error);
                                });
                            }).catch(error => {
                                console.error(error);
                            })
                        }).catch(error => {
                            console.error(error);
                        })
                    }).catch(error => {
                        if (error.response?.data?.status === 400 && error.response?.data?.message === "Title already exists.") {
                            console.error(error);
                            dispatch({
                                type: UiActionType.SNACKBAR_STATE_CHANGED,
                                newState: {
                                    open: true,
                                    message: `Could not create title. A title with Title Number ${title.titleNumber} already exists. Please use a different Title Number.`,
                                    severity: "error"
                                }
                            });
                        } else {
                            console.error(error);
                            dispatch({
                                type: UiActionType.SNACKBAR_STATE_CHANGED,
                                newState: {
                                    open: true,
                                    message: "Could not create title",
                                    severity: "error"
                                }
                            });
                        }
                    })
                }}
                onCancel={() => {
                    setAddTitleDialogOpen(false);
                }}
            />
            <Grid container direction='column' className={`${classes.root} flex-gap-4`} alignItems="center">
                <Grid item>
                    <Typography variant='h1'>Title Status</Typography>
                </Grid>
                {[UserRole.ADMINISTRATOR].includes(currentUser.userRole) &&
                    <Grid item>
                        <Grid container direction='row' className="flex-gap-1">
                            <Grid item>
                                <Button color='primary' size='small' variant='contained' disableElevation onClick={() => { setResetStatus(TitleStatus.WAITING_FOR_ASSIGNMENT); setResetDialogOpen(true); }}>Reset all to Waiting for Assignment</Button>
                            </Grid>
                            <Grid item>
                                <Button color='primary' size='small' variant='contained' disableElevation onClick={() => { setResetStatus(TitleStatus.ASSIGNED); setResetDialogOpen(true); }}>Reset all to Assigned</Button>
                            </Grid>
                            <Grid item>
                                <Button color='primary' size='small' variant='contained' disableElevation onClick={() => { setAddTitleDialogOpen(true); }}>Add Title</Button>
                            </Grid>
                        </Grid>
                    </Grid>
                }
                <Grid item>
                    <div className={classes.tableWrapper}>
                        <MUIDataTable
                            title=""
                            columns={columns}
                            data={records}
                            options={{
                                filter: false,
                                search: false,
                                print: false,
                                download: false,
                                viewColumns: false,
                                selectableRows: undefined,
                                sortOrder: {
                                    name: 'titleNumber',
                                    direction: 'asc'
                                },
                                rowsPerPage: 1000,
                                elevation: 0,
                                setTableProps: () => {
                                    return {
                                        size: "small",
                                        padding: "1px"
                                    }
                                }
                            }}
                        />
                    </div>
                </Grid>
            </Grid>
        </>
    )
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        currentUser: state.user.currentUser
    };
};

const mapDispatchToProps = (dispatch: any) => {
    return { dispatch };
}

export default compose<any>(
    connect(mapStateToProps, mapDispatchToProps)
)(TitleStatusPage);
